<template>
  <div>
    <v-row class="mb-4">
      <v-col>
        <h3 class="text-h6 font-weight-medium mb-4">
          Alias Settings
        </h3>
        <p class="text--secondary">
          View the app as an external user to see what they see.
        </p>
      </v-col>
    </v-row>

    <!-- Active alias banner (shown when aliasing but without permission to change) -->
    <v-alert
      v-if="inAliasMode && !hasAliasPermission"
      type="info"
      outlined
      class="mb-4"
    >
      This account is aliasing <strong>{{ aliasEmail }}</strong>.
    </v-alert>

    <!-- Alias controls (shown for users with permission) -->
    <template v-if="hasAliasPermission">
      <v-row>
        <v-col
          cols="12"
          md="6"
        >
          <v-combobox
            v-model="selectedEmail"
            :items="emailOptions"
            :loading="loadingOptions"
            label="User Email"
            placeholder="Select an external user..."
            outlined
            clearable
            @change="onEmailChange"
          >
            <template v-slot:prepend-inner>
              <font-awesome-icon :icon="['fas', 'user-secret']" />
            </template>
          </v-combobox>
        </v-col>
      </v-row>

      <v-row class="mb-4">
        <v-col class="d-flex align-center justify-space-between">
          <v-switch
            v-model="aliasActive"
            active-class="primary--text"
            hide-details
            :label="aliasActive ? 'Alias On' : 'Alias Off'"
            inset
            class="mt-0"
          />
          <v-btn
            color="primary"
            outlined
            class="text-none"
            @click="saveAlias"
          >
            <font-awesome-icon
              :icon="['fas', 'floppy-disk']"
              size="sm"
              class="mr-2"
            />
            Save
          </v-btn>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')

export default {
  name: 'SettingsAlias',
  data() {
    return {
      emailOptions: [],
      loadingOptions: false,
      selectedEmail: null,
      aliasActive: false,
    }
  },
  computed: {
    ...mapAuthGetters([
      'aliasEmail',
      'hasAliasPermission',
      'inAliasMode',
      'isAliasActive',
    ]),
  },
  watch: {
    hasAliasPermission: {
      immediate: true,
      async handler(hasPermission) {
        if (hasPermission && !this.emailOptions.length) {
          await this.fetchAliasOptions()
        }
      },
    },
  },
  created() {
    // Initialize from current alias state
    this.selectedEmail = this.aliasEmail || null
    this.aliasActive = this.isAliasActive || false
  },
  methods: {
    async fetchAliasOptions() {
      this.loadingOptions = true
      try {
        const externals = await this.$store.dispatch('auth/getAliasOptions', null)
        this.emailOptions = (externals || []).map((ext) => ({
          text: ext.docId,
          value: ext.docId,
        }))
      } catch (error) {
        this.$log.error('Failed to load alias options:', error)
      } finally {
        this.loadingOptions = false
      }
    },
    onEmailChange(selection) {
      this.selectedEmail = selection?.value || selection || null
    },
    saveAlias() {
      const alias = {
        active: this.aliasActive,
        email: this.selectedEmail,
      }
      this.$store.dispatch('auth/updateAliasMode', alias, { root: true })
    },
  },
}
</script>
